import { useEffect, useRef, useState } from "react";
import { io } from "socket.io-client";
import { MessageCircle, X, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, SOCKET_URL, SOCKET_PATH } from "@/lib/api";

const STORAGE_KEY = "le_chat_session";

const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [session, setSession] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
    } catch {
      return null;
    }
  });
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [connected, setConnected] = useState(false);
  const socketRef = useRef(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (!session) return;

    let cancelled = false;
    api.get(`/chat/sessions/${session.id}/messages`).then((res) => {
      if (!cancelled) setMessages(res.data);
    });

    socketRef.current = io(SOCKET_URL, { path: SOCKET_PATH, transports: ["websocket", "polling"] });
    socketRef.current.on("connect", () => {
      setConnected(true);
      socketRef.current.emit("join_room", { room_id: session.id });
    });
    socketRef.current.on("disconnect", () => setConnected(false));
    socketRef.current.on("new_message", (msg) => {
      if (msg.session_id === session.id) {
        setMessages((prev) => (prev.find((m) => m.id === msg.id) ? prev : [...prev, msg]));
      }
    });

    return () => {
      cancelled = true;
      if (socketRef.current?.connected) socketRef.current.disconnect();
    };
  }, [session]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  const startChat = async (e) => {
    e?.preventDefault();
    const visitorName = name.trim() || "Visitor";
    const res = await api.post("/chat/sessions", { visitor_name: visitorName });
    localStorage.setItem(STORAGE_KEY, JSON.stringify(res.data));
    setSession(res.data);
  };

  const send = (e) => {
    e?.preventDefault();
    const t = text.trim();
    if (!t || !socketRef.current?.connected || !session) return;
    socketRef.current.emit("send_message", {
      session_id: session.id,
      sender: "visitor",
      sender_name: session.visitor_name,
      text: t,
    });
    setText("");
  };

  const resetChat = () => {
    localStorage.removeItem(STORAGE_KEY);
    setSession(null);
    setMessages([]);
    setName("");
  };

  return (
    <>
      <button
        onClick={() => setOpen((s) => !s)}
        data-testid="chat-trigger-btn"
        className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-secondary text-secondary-foreground shadow-xl hover:shadow-2xl hover:-translate-y-0.5 transition-all flex items-center justify-center"
        aria-label={open ? "Close chat with the township" : "Open chat with the township"}
        aria-expanded={open}
        aria-controls="chat-dialog"
      >
        {open ? <X size={22} aria-hidden="true" /> : <MessageCircle size={22} aria-hidden="true" />}
      </button>

      {open && (
        <div
          id="chat-dialog"
          role="dialog"
          aria-label="Chat with Lake Eunice Township"
          className="fixed bottom-24 right-6 z-50 w-[22rem] max-w-[calc(100vw-2rem)] h-[28rem] bg-white rounded-2xl shadow-2xl border border-border flex flex-col overflow-hidden fade-up"
          data-testid="chat-window"
        >
          <div className="bg-primary text-primary-foreground p-4 flex justify-between items-center">
            <div>
              <div className="font-heading text-base font-semibold">Chat with the Township</div>
              <div className="text-xs text-primary-foreground/70">
                {session ? (connected ? "Connected · live" : "Reconnecting...") : "We typically reply within a day"}
              </div>
            </div>
            <button onClick={() => setOpen(false)} className="p-1 rounded hover:bg-white/10" aria-label="Close" data-testid="chat-close-btn">
              <X size={18} />
            </button>
          </div>

          {!session ? (
            <form onSubmit={startChat} className="flex-1 p-5 flex flex-col justify-center gap-4 bg-muted/40">
              <div>
                <div className="font-heading text-lg font-semibold mb-1">Send us a message</div>
                <p className="text-sm text-muted-foreground">Leave your first name so we know who we’re talking with.</p>
              </div>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                data-testid="chat-name-input"
              />
              <Button type="submit" className="bg-primary hover:bg-primary/90" data-testid="chat-start-btn">
                Start conversation
              </Button>
            </form>
          ) : (
            <>
              <div
                ref={scrollRef}
                className="flex-1 overflow-y-auto p-4 bg-muted/30 space-y-3"
                data-testid="chat-messages"
                role="log"
                aria-live="polite"
                aria-label="Chat messages"
              >
                {messages.length === 0 && (
                  <div className="text-center text-xs text-muted-foreground py-6">
                    Say hello — the township will reply here when an admin is online.
                  </div>
                )}
                {messages.map((m) => {
                  const isVisitor = m.sender === "visitor";
                  return (
                    <div
                      key={m.id}
                      className={`flex ${isVisitor ? "justify-end" : "justify-start"}`}
                      data-testid={`chat-msg-${m.sender}`}
                    >
                      <div
                        className={`max-w-[80%] rounded-2xl px-3.5 py-2 text-sm leading-snug shadow-sm ${
                          isVisitor
                            ? "bg-secondary text-secondary-foreground rounded-br-sm"
                            : "bg-white border border-border rounded-bl-sm"
                        }`}
                      >
                        <div className={`text-[10px] uppercase tracking-wider mb-1 ${isVisitor ? "text-secondary-foreground/70" : "text-muted-foreground"}`}>
                          {m.sender_name}
                        </div>
                        <div>{m.text}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <form onSubmit={send} className="p-3 border-t border-border bg-white flex gap-2">
                <Input
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder="Type a message..."
                  data-testid="chat-text-input"
                />
                <Button type="submit" size="icon" className="bg-primary hover:bg-primary/90 shrink-0" data-testid="chat-send-btn" aria-label="Send">
                  <Send size={16} />
                </Button>
              </form>
              <button
                onClick={resetChat}
                className="text-[11px] text-muted-foreground hover:text-foreground py-1.5 border-t border-border bg-muted/40"
                data-testid="chat-reset-btn"
              >
                Start a new conversation
              </button>
            </>
          )}
        </div>
      )}
    </>
  );
};

export default ChatWidget;
